import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type {
  ProfitMarginDirection,
  ProfitMarginDirectionConfig,
  ProfitMarginInput,
  ProfitMarginType,
} from "@/services/Market.service";
import { ArrowRight, Loader2 } from "lucide-react";
import { formatMargin } from "./market-margin.utils";

type ProfitMarginCardProps = {
  direction: ProfitMarginDirection;
  title: string;
  config: ProfitMarginDirectionConfig;
  draft: ProfitMarginInput;
  error: string | null;
  previewError: string | null;
  proposed: ProfitMarginDirectionConfig | null;
  canEdit: boolean;
  isPreviewing: boolean;
  isPreviewValid: boolean;
  hasChanges: boolean;
  onChange: (value: ProfitMarginInput) => void;
  onReview: () => void;
};

function formatNgn(value: string | null): string {
  if (value === null) return "Unavailable";
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value));
}

export function ProfitMarginCard({
  direction,
  title,
  config,
  draft,
  error,
  previewError,
  proposed,
  canEdit,
  isPreviewing,
  isPreviewValid,
  hasChanges,
  onChange,
  onReview,
}: ProfitMarginCardProps) {
  const providerName = config.provider === "quidax" ? "Quidax" : "Treasura";
  const directionCopy =
    direction === "onRamp"
      ? "Added to the provider rate when customers buy crypto."
      : "Deducted from the provider rate when customers sell crypto.";

  const changeType = (marginType: ProfitMarginType) => {
    onChange({ ...draft, marginType });
  };

  return (
    <section className="rounded-xl border border-[#EAE6EA] bg-card p-6 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-primary-3">{title}</h2>
          <p className="mt-1 text-sm text-gray-70">Provider: {providerName}</p>
        </div>
        <span className="rounded-full bg-[#F3F0F3] px-3 py-1 text-xs font-medium text-primary-3">
          {direction === "onRamp" ? "Customer buys" : "Customer sells"}
        </span>
      </div>

      <p className="mt-4 text-sm text-gray-70">{directionCopy}</p>

      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        <div className="space-y-2">
          <label
            className="text-sm font-medium text-primary-3"
            htmlFor={`${direction}-type`}
          >
            Margin Type
          </label>
          <Select
            value={draft.marginType}
            onValueChange={changeType}
            disabled={!canEdit}
          >
            <SelectTrigger id={`${direction}-type`} className="h-11 rounded-lg">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="FLAT">Flat</SelectItem>
              <SelectItem value="PERCENTAGE">Percentage</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <label
            className="text-sm font-medium text-primary-3"
            htmlFor={`${direction}-value`}
          >
            Margin Value
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-70">
              {draft.marginType === "FLAT" ? "₦" : "%"}
            </span>
            <Input
              id={`${direction}-value`}
              inputMode="decimal"
              value={draft.marginValue}
              onChange={(event) =>
                onChange({ ...draft, marginValue: event.target.value.trim() })
              }
              disabled={!canEdit}
              aria-invalid={Boolean(error)}
              className="h-11 rounded-lg pl-8"
              placeholder={draft.marginType === "FLAT" ? "10.00" : "1.50"}
            />
          </div>
          <p className="text-xs text-gray-70">
            {draft.marginType === "FLAT"
              ? "Naira adjustment per $1 of the exchange rate"
              : "Percentage of the provider exchange rate"}
          </p>
          {error ? <p className="text-xs text-red-600">{error}</p> : null}
        </div>
      </div>

      <div className="mt-6 rounded-lg bg-[#F8F7F8] p-4">
        <p className="text-xs font-medium uppercase tracking-wide text-gray-70">
          Current
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
          <div>
            <p className="text-xs text-gray-70">Provider rate</p>
            <p className="mt-1 font-semibold text-primary-3">
              {formatNgn(config.providerRate)}
            </p>
          </div>
          <ArrowRight className="h-4 w-4 text-gray-50" aria-hidden="true" />
          <div>
            <p className="text-xs text-gray-70">Customer rate</p>
            <p className="mt-1 font-semibold text-primary-3">
              {formatNgn(config.customerRate)}
            </p>
          </div>
        </div>
        <p className="mt-3 text-xs text-gray-70">
          Current margin: {formatMargin(config)}
        </p>
      </div>

      {canEdit && hasChanges ? (
        <div className="mt-3 rounded-lg border border-[#E8D99C] bg-[#FFF9E8] p-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs font-medium uppercase tracking-wide text-[#6B5312]">
              Proposed
            </p>
            {isPreviewing ? (
              <span className="flex items-center text-xs text-[#6B5312]">
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                Checking rate
              </span>
            ) : null}
          </div>
          {proposed && isPreviewValid ? (
            <>
              <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
                <div>
                  <p className="text-xs text-gray-70">Provider rate</p>
                  <p className="mt-1 font-semibold text-primary-3">
                    {formatNgn(proposed.providerRate)}
                  </p>
                </div>
                <ArrowRight
                  className="h-4 w-4 text-gray-50"
                  aria-hidden="true"
                />
                <div>
                  <p className="text-xs text-gray-70">Customer rate</p>
                  <p className="mt-1 font-semibold text-primary-3">
                    {formatNgn(proposed.customerRate)}
                  </p>
                </div>
              </div>
              <p className="mt-3 text-xs text-gray-70">
                Proposed margin: {formatMargin(proposed)}
              </p>
            </>
          ) : !isPreviewing ? (
            <p className="mt-3 text-xs text-red-600">
              {previewError ?? "Waiting for a valid backend rate preview."}
            </p>
          ) : null}
        </div>
      ) : null}

      {canEdit ? (
        <div className="mt-6 flex justify-end">
          <Button
            type="button"
            onClick={onReview}
            disabled={
              !hasChanges || Boolean(error) || isPreviewing || !isPreviewValid
            }
            className="min-w-36 rounded-lg"
          >
            {isPreviewing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Checking impact
              </>
            ) : (
              "Review change"
            )}
          </Button>
        </div>
      ) : null}
    </section>
  );
}
